import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { KeycloakService } from 'keycloak-angular';
import { from, switchMap, catchError, of, throwError } from 'rxjs';

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const keycloakService = inject(KeycloakService);

  console.log('[AuthInterceptor] Intercepting request:', req.url);

  // Nur für API-Requests
  if (!req.url.includes('/api/')) {
    console.log('[AuthInterceptor] Not an API request, skipping');
    return next(req);
  }

  // Nur für Admin-Endpunkte Token hinzufügen
  const isAdminEndpoint = req.url.includes('/api/admin') || 
                          req.url.includes('/api/mail') ||
                          req.url.includes('/api/contact/admin') ||
                          req.url.includes('/order-confirmation/sevdesk');

  console.log('[AuthInterceptor] Is admin endpoint?', isAdminEndpoint, 'for URL:', req.url);

  if (!isAdminEndpoint) {
    console.log('[AuthInterceptor] Not an admin endpoint, skipping token');
    return next(req);
  }

  // Prüfen, ob User eingeloggt ist
  if (!keycloakService.isLoggedIn()) {
    console.warn('[AuthInterceptor] User not logged in, request without token:', req.url);
    return next(req);
  }

  // Token holen und zum Request hinzufügen
  return from(keycloakService.getToken()).pipe(
    switchMap(token => {
      if (token) {
        // Token inspizieren für Debugging
        try {
          const tokenParts = token.split('.');
          if (tokenParts.length === 3) {
            const decoded = JSON.parse(atob(tokenParts[1]));
            console.log('[AuthInterceptor] Token Details for', req.url, {
              sub: decoded.sub,
              aud: decoded.aud,
              iss: decoded.iss,
              preferred_username: decoded.preferred_username,
              exp: new Date(decoded.exp * 1000).toISOString(),
              roles: decoded.resource_access?.['videowand-backend']?.roles || []
            });
          }
        } catch (e) {
          console.error('[AuthInterceptor] Failed to decode token:', e);
        }

        console.log('[AuthInterceptor] Adding token to request:', req.url);
        const clonedReq = req.clone({
          setHeaders: {
            Authorization: `Bearer ${token}`
          }
        });
        return next(clonedReq).pipe(
          catchError(error => {
            // Bei 401 Fehler: Token refresh versuchen
            if (error.status === 401) {
              console.warn('[AuthInterceptor] Got 401, attempting token refresh:', req.url);
              return from(keycloakService.updateToken(5)).pipe(
                switchMap(tokenRefreshed => {
                  if (tokenRefreshed) {
                    return from(keycloakService.getToken()).pipe(
                      switchMap(newToken => {
                        console.log('[AuthInterceptor] Token refreshed, retrying request:', req.url);
                        const retryReq = req.clone({
                          setHeaders: {
                            Authorization: `Bearer ${newToken}`
                          }
                        });
                        return next(retryReq);
                      })
                    );
                  }
                  console.error('[AuthInterceptor] Token refresh failed, token still invalid');
                  return throwError(() => error);
                }),
                catchError(refreshError => {
                  console.error('[AuthInterceptor] Error refreshing token:', refreshError);
                  return throwError(() => error);
                })
              );
            }
            return throwError(() => error);
          })
        );
      }
      console.warn('[AuthInterceptor] No token available for request:', req.url);
      return next(req);
    }),
    catchError(error => {
      console.error('[AuthInterceptor] Error getting token:', error);
      // Request auch ohne Token versuchen
      return next(req);
    })
  );
};

